import React from 'react';
import { Target, TrendingUp, Award, Users, Lightbulb, Rocket } from 'lucide-react';

const AboutSection = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: 'Innovation',
      description: 'Leveraging cutting-edge AI and technology to deliver forward-thinking solutions'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Growth',
      description: 'Focused on measurable results that scale your business and online presence'
    },
    {
      icon: <Award size={32} />,
      title: 'Excellence',
      description: 'Committed to delivering high-quality work that exceeds expectations'
    }
  ];

  const capabilities = [
    {
      icon: <Lightbulb size={40} />,
      title: 'Creative Solutions',
      description: 'Innovative approaches tailored to your unique business needs'
    },
    {
      icon: <Users size={40} />,
      title: 'Expert Team',
      description: 'Experienced professionals dedicated to your success'
    },
    {
      icon: <Rocket size={40} />,
      title: 'Fast Delivery',
      description: 'Quick turnaround without compromising on quality'
    }
  ];

  return (
    <section id="about" style={{ background: 'var(--bg-page)', padding: '7rem 1.5rem 5rem' }}>
      <div className="container">
        {/* Main About Content */}
        <div style={{ textAlign: 'center', marginBottom: '4rem', maxWidth: '800px', margin: '0 auto 4rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
            About ibTECON
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            We are a distinguished digital solutions company committed to assisting businesses in establishing a robust online presence. Our comprehensive services encompass website development and social media growth, all aimed at delivering measurable outcomes that enhance visibility, credibility, and profitability. Furthermore, ibTECON specializes in IT services as well as corporate support staffing.
          </p>
        </div>

        {/* Visual Divider */}
        <div style={{
          width: '100px',
          height: '4px',
          background: 'var(--gradient-button)',
          margin: '3rem auto',
          borderRadius: '2px'
        }} />

        {/* Our Values Section */}
        <div style={{ marginBottom: '5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              Our Core Values
            </h3>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              The principles that drive everything we do
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {values.map((value, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, rgba(51, 204, 204, 0.05) 0%, rgba(218, 229, 239, 0.15) 100%)',
                  border: '2px solid var(--border-light)',
                  borderRadius: '20px',
                  padding: '2.5rem 2rem',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(51, 204, 204, 0.2)';
                  e.currentTarget.style.borderColor = 'var(--accent-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'var(--border-light)';
                }}
              >
                {/* Decorative corner element */}
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '60px',
                  height: '60px',
                  background: 'var(--accent-wash)',
                  borderRadius: '50%',
                  opacity: 0.5
                }} />
                
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'var(--gradient-button)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    color: 'white',
                    boxShadow: '0 8px 20px rgba(51, 204, 204, 0.3)'
                  }}
                >
                  {value.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                  {value.title}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* What Sets Us Apart */}
        <div style={{ background: 'var(--bg-section)', borderRadius: '24px', padding: '4rem 2rem', position: 'relative', overflow: 'hidden' }}>
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            left: '-50px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(51, 204, 204, 0.1) 0%, transparent 70%)',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-80px',
            right: '-80px',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(36, 67, 89, 0.08) 0%, transparent 70%)',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h3 className="heading-2" style={{ marginBottom: '1rem', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                What Sets Us Apart
              </h3>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Our unique approach to digital solutions
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2.5rem', maxWidth: '1000px', margin: '0 auto' }}>
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '2rem',
                    textAlign: 'center',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'default',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(51, 204, 204, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <div style={{ color: 'var(--accent-text)', marginBottom: '1rem' }}>
                    {capability.icon}
                  </div>
                  <h4 style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                    {capability.title}
                  </h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {capability.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;